/**
 * @fileoverview Component for displaying a study card.
 * This component includes a header with a back button, a study name, and a tabs component.
 */

import { Outlet, useLocation, useParams } from 'react-router-dom'
import BackIcon from '../../assets/back.svg'
import StudyIcon from '../../assets/menu/studies.svg'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useGetStudyByIdQuery } from '../../services/studies'
import Tabs from '../elements/Tabs'

/**
 * @function hasPermission
 * @description Checks if the user has a given permission.
 * @param {string[]} permissions - The user's permissions.
 * @param {string} permission - The permission to check for.
 * @returns {boolean} - Whether the user has the permission.
 */
function hasPermission(permissions: string[] = [], permission: string): boolean {
    return permissions.includes(permission)
}

/**
 * @function StudyCard
 * @description Component for displaying a study card.
 * @returns {JSX.Element} - A React component that renders the study card.
 */
export function StudyCard() {
    const params = useParams()
    const location = useLocation()
    const tab = location.pathname.split('/').pop()
    const [, get] = useLocalStorage()
    const user = get<Novoic.API.User>('user')
    const permissions = user?.permissions
    const { data: study } = useGetStudyByIdQuery(params.studyId)

    const tabs = [
        {
            name: 'Participants',
            href: 'participants',
            current: tab === 'participants',
        },
        hasPermission(permissions, 'SITE.LIST') && {
            name: 'Sites',
            href: 'sites',
            current: tab === 'sites',
        },
        {
            name: 'Links',
            href: 'links',
            current: tab === 'links',
        },
    ].filter((x) => x)

    return (
        <section>
            <section className="bg-white sticky top-0 w-full divide-y divide-gray-200 border-b-gray-200 border-b">
                <div className="flex items-center h-[88px] px-8">
                    <BackIcon
                        onClick={() => history.back()}
                        className="cursor-pointer mr-6 fill-gray-500 hover:fill-gray-900 transition-colors"
                    />
                    <div className="flex shrink-0 mr-3 justify-center items-center w-10 h-10 rounded-full bg-indigo-50">
                        <StudyIcon className="stroke-indigo-500" />
                    </div>
                    <span className="text-2xl font-semibold mr-3">{study?.name}</span>
                </div>
                <section className="px-8 py-6 h-[88px]">
                    <Tabs tabs={tabs} />
                </section>
            </section>
            <section>
                <Outlet />
            </section>
        </section>
    )
}
